import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DeviceUnpairIcon,
  FindMeIcon,
  LatestActionIcon,
  NewDoneIcon,
  UnpairedIcon,
} from '../Common/Icons';
import { StatusInfo } from './DeviceInventoryList';
import { getConfirmationMessage, isDeviceOffline } from './DeviceInventoryModal';
import {
  canShowUnpairAction,
  formatTimestamp,
  hasStaffPrivileges,
  rcEventTypes,
  RCKeyButtonMap,
  useRCEventStream,
  useUserEvents,
  useUserRole,
} from './helpers';
import RemoteControlLayout from './RemoteControlLayout';

const eventTypes = [...Object.values(rcEventTypes)];

const RemoteControlModalInfo = ({
  device,
  hubRecord,
  providerid,
  performAction,
  actionsData,
  hideModal,
  ConfirmationModal,
  isActionModalVisible,
  isTVDevice,
  activeDeviceAction,
  handleDeviceActionClick,
  timezone,
  deviceStatus,
  loginUserTimeformat,
}) => {
  const trans = useSelector(translator);
  const userRole = useUserRole();
  const hubRoleName = hubRecord?.rolename?.toLowerCase();

  const allowFindMeAction = useMemo(() => ['manage', 'care'].includes(hubRoleName), [hubRoleName]);

  const allowUnpairAction = useMemo(
    () => canShowUnpairAction(userRole, hubRoleName),
    [userRole, hubRoleName]
  );

  const [actionAndOrientation, setActionAndOrientation] = useState({
    action: '',
    actionTimestamp: '',
    key: '',
    orientation: '',
  });

  useUserEvents(device, hubRecord, setActionAndOrientation);

  useRCEventStream(hubRecord, eventTypes, setActionAndOrientation);

  const showKeyAction = useMemo(
    () => !isEmpty(actionAndOrientation.action) && !isEmpty(actionAndOrientation.key),
    [actionAndOrientation.action, actionAndOrientation.key]
  );

  const showOrientation = useMemo(
    () => !isEmpty(actionAndOrientation.orientation),
    [actionAndOrientation.orientation]
  );

  const showActionsBloc =
    (allowFindMeAction || hasStaffPrivileges(userRole)) && device?.paired_status !== 'UNPAIRED';

  const handleUnpair = (device) => {
    let data = {
      ...actionsData.unpair_rcu,
      deviceUuid: hubRecord?.deviceuuid,
    };
    const params =
      actionsData.unpair_rcu.action === 'perform_unPairRCU'
        ? {
            params: [
              { name: 'devicemodel', value: device?.modelnumber },
              { name: 'macaddress', value: device?.device_mac_address },
            ],
          }
        : {};
    performAction(data, params);
    hideModal('unpair_rcu');
  };

  const handleFindMe = (device) => {
    let data = {
      ...actionsData.find_me,
      deviceUuid: hubRecord?.deviceuuid,
    };
    performAction(data, null);
    hideModal('find_me');
  };

  // Extract the last orientation from device_motion_events
  const lastMotionEvent = device?.device_motion_events
    ? JSON.parse(device.device_motion_events).slice(-1)[0]
    : null;

  const lastOrientation = lastMotionEvent?.orientation || '-';

  // Define the constant map for device information
  const tvInfoMap = [
    {
      key: trans('BRAND'),
      value: device?.device_brand_id ? device.device_brand_id : 'not set',
    },
    {
      key: trans('REMOTEIRCODE'),
      value: device?.device_codeset_id ? device.device_codeset_id : 'not set',
    },
  ];

  return (
    <div className="rc400-bloc-bottom">
      <div
        className={`bloc-left ${
          device?.paired_status === 'UNPAIRED' ? 'device-details-transparent' : ''
        }`}
      >
        <div className="tv-configuration-block">
          <div className="configuration-block-container">
            <div className="tv-custom-title-label">{trans('TVCONFIGURATION')}</div>
            <div className="configuration-details-block">
              <div className="done-block">
                {isTVDevice?.device_learning_type === null ||
                isTVDevice?.device_learning_type === undefined ||
                isTVDevice?.device_learning_type === 'NONE' ? (
                  <UnpairedIcon label={trans('NOTDONE')} />
                ) : (
                  <NewDoneIcon label={trans('DONE')} />
                )}
              </div>
              {tvInfoMap.map((item, index) => (
                <div key={index} className="tv-info-row">
                  <span className="tv-info-key">{item.key}:</span>
                  <span className="tv-info-value"> {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {showActionsBloc && (
          <div className="actions-bloc">
            <div className="actions-bloc-container">
              <div className="actions-main">
                <div className="device-action-label">{trans('ACTIONS')}</div>
                <div className="device-page-description-container">
                  {allowFindMeAction && (
                    <div className="find-me">
                      {trans('“FINDME”')}
                      <span className="find-me-desc">
                        {trans('ACTIVATESABEEPINGFOR30SECONTHEREMOTE')}
                      </span>
                    </div>
                  )}
                  {device?.paired_status !== 'UNPAIRED' && allowUnpairAction && (
                    <div className="unpair">
                      {trans('“UNPAIR”')}
                      <span className="unpair-desc">
                        {trans(':UNPAIRTHESENSORBYCLICKINGONUNPAIRBUTTON')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="action-bottom">
                {allowFindMeAction && (
                  <Tooltip
                    title={
                      isDeviceOffline(device) || deviceStatus === 'disconnected' ? (
                        <StatusInfo
                          lastConnectTime={device?.lastconnecttime}
                          lastDisconnectTime={device?.lastdisconnecttime}
                          device={device}
                          timezone={timezone}
                          loginUserTimeformat={loginUserTimeformat}
                          showDuration={false}
                          tooltipMode={true}
                          trans={trans}
                        />
                      ) : (
                        ''
                      )
                    }
                  >
                    <span>
                      <div
                        className={`find-me-button ${
                          isDeviceOffline(device) || deviceStatus === 'disconnected'
                            ? 'device-page-actions-disabled'
                            : ''
                        }`}
                        onClick={() =>
                          !isDeviceOffline(device) &&
                          deviceStatus !== 'disconnected' &&
                          handleDeviceActionClick('find_me')
                        }
                      >
                        <FindMeIcon
                          strokeColor={
                            isDeviceOffline(device) || deviceStatus === 'disconnected'
                              ? '#808080'
                              : '#005D98'
                          }
                          className="find-me-icon"
                        />
                        <div className="action-label">{trans('FINDME_U')}</div>
                      </div>
                    </span>
                  </Tooltip>
                )}
                {device?.paired_status !== 'UNPAIRED' && allowUnpairAction && (
                  <Tooltip
                    title={
                      deviceStatus === 'disconnected' ? (
                        <StatusInfo
                          lastConnectTime={device?.lastconnecttime}
                          lastDisconnectTime={device?.lastdisconnecttime}
                          device={device}
                          timezone={timezone}
                          loginUserTimeformat={loginUserTimeformat}
                          showDuration={false}
                          tooltipMode={true}
                          trans={trans}
                        />
                      ) : (
                        ''
                      )
                    }
                  >
                    <span>
                      <div
                        className={`unpair-button ${
                          deviceStatus === 'disconnected' ? 'device-page-actions-disabled' : ''
                        }`}
                        onClick={() =>
                          deviceStatus !== 'disconnected' && handleDeviceActionClick('unpair_rcu')
                        }
                      >
                        <DeviceUnpairIcon
                          className="unpair-icon"
                          strokeColor={deviceStatus === 'disconnected' ? '#808080' : '#005D98'}
                          fillColor={deviceStatus === 'disconnected' ? '#808080' : '#0072BC'}
                        />
                        <div className="action-label">{trans('UNPAIR')}</div>
                      </div>
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        className={`device-bloc ${showKeyAction || showOrientation ? 'large' : 'small'} ${
          device?.paired_status === 'UNPAIRED' ||
          isDeviceOffline(device) ||
          deviceStatus === 'disconnected'
            ? 'device-details-transparent'
            : ''
        }`}
      >
        <div className="top-info">
          <div className="device-model-label">{trans('LIVEVIEW')}</div>
          {showKeyAction && (
            <div className="latest-action">
              <LatestActionIcon className="latest-action-icon" />
              <div className="latest-action-title">
                {trans('LATESTACTION')}
                <span className="latest-action-subtitle">
                  {/* {device?.device_key_name
                    ? `${device.device_key_name} ${trans('PRESSED_L')} `
                    : '-'} */}
                  {/* {` ${actionAndOrientation.action}`} */}
                  <span className="highlight-text">
                    {/* {device?.device_key_action ? `“${device.device_key_action}”` : '-'} */}
                    {` ${RCKeyButtonMap[actionAndOrientation.key]}`}
                  </span>
                  {` (${formatTimestamp(actionAndOrientation.actionTimestamp, timezone)})`}
                </span>
              </div>
            </div>
          )}
          <div className="remote-container">
            <RemoteControlLayout
              rcKey={actionAndOrientation.key}
              action={actionAndOrientation.action}
              hubRecord={hubRecord}
              providerid={providerid}
            />
          </div>
          {showOrientation && (
            <div className="subtitle-info">
              <div className="current-orientation-label">
                {trans('CURRENTORIENTATION')}
                <span className="current-orientation-subtitle">
                  {/* {` ${lastOrientation}`} */}
                  {` ${actionAndOrientation.orientation}`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={isActionModalVisible[activeDeviceAction]}
        title={
          activeDeviceAction !== 'find_me' ? (
            <span className="custom-modal-title">
              {getConfirmationMessage(actionsData[activeDeviceAction]?.title?.toLowerCase(), trans)}
            </span>
          ) : (
            <span className="custom-modal-title">{trans('AREYOUSUREYOUWANTTOFINEMYREMOTE?')}</span>
          )
        }
        description={actionsData[activeDeviceAction]?.description}
        onConfirm={() =>
          activeDeviceAction === 'find_me' ? handleFindMe(device) : handleUnpair(device)
        }
        onCancel={() => hideModal(activeDeviceAction)}
        customLabel={actionsData[activeDeviceAction]?.title}
        customIcon={
          activeDeviceAction === 'find_me' ? (
            <FindMeIcon strokeColor="#113E57" />
          ) : (
            <DeviceUnpairIcon fillColor="#113E57" strokeColor="#113E57" />
          )
        }
        activeDeviceAction={activeDeviceAction}
      />
    </div>
  );
};

export default RemoteControlModalInfo;
